/**
 * Phase 7H — Orders Workspace (Unified Orders Review)
 *
 * Aggregates ALL pending clinical items across My Day buckets for
 * unified review and batch sign-off. Entry point: /orders
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Pill, FlaskConical, ListChecks, FileText, Check, Clock, Loader2,
  AlertCircle, ArrowLeft, Send, X, User
} from 'lucide-react';
import { toast } from 'sonner';

const SECTIONS = [
  { key: 'prescriptions',  label: 'Prescriptions / Refills', icon: Pill,          color: 'border-violet-200 bg-violet-50/30' },
  { key: 'labs_imaging',    label: 'Labs / Imaging',          icon: FlaskConical,  color: 'border-blue-200 bg-blue-50/30' },
  { key: 'tasks',           label: 'Tasks',                   icon: ListChecks,    color: 'border-amber-200 bg-amber-50/30' },
  { key: 'correspondence',  label: 'Correspondence',          icon: FileText,      color: 'border-teal-200 bg-teal-50/30' },
];

export default function OrdersWorkspace() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(false);

  const fetchData = async () => {
    try {
      const res = await api.get('/orders/workspace');
      setData(res.data);
    } catch (err) {
      console.error('Failed to load orders workspace:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleBulkFile = async (sectionKey) => {
    if (!data) return;
    const section = data.sections[sectionKey];
    const fileableIds = (section?.items || [])
      .filter(i => i.status !== 'completed' && i.status !== 'pending_nurse' && i.linked_diagnosis)
      .map(i => i.id);

    if (fileableIds.length === 0) {
      toast.error('No items ready to file (check diagnosis linkage)');
      return;
    }

    setBulkLoading(true);
    try {
      const res = await api.post('/orders/workspace/bulk-action', {
        action: 'file', item_ids: fileableIds, notes: 'Batch filed from Orders Workspace',
      });
      toast.success(`Filed ${res.data.executed} of ${res.data.total} items`);
      fetchData();
    } catch { toast.error('Bulk action failed'); }
    finally { setBulkLoading(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  const progress = data?.progress || {};

  return (
    <div className="flex flex-col h-full" data-testid="orders-workspace-page">
      {/* Header */}
      <div className="px-5 py-3 border-b bg-card flex items-center gap-3 shrink-0">
        <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" onClick={() => navigate('/my-day')}
          data-testid="orders-back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> My Day
        </Button>
        <div className="h-5 w-px bg-border" />
        <h2 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Orders & Actions Review
        </h2>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <div className="w-20 h-1.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: `${progress.total > 0 ? Math.round(((progress.total - progress.pending) / progress.total) * 100) : 100}%` }} />
          </div>
          <span className="text-[10px] text-muted-foreground tabular-nums">
            {progress.pending || 0} pending
          </span>
          {progress.missing_diagnosis > 0 && (
            <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-600 border-amber-200 gap-0.5">
              <AlertCircle className="w-2.5 h-2.5" /> {progress.missing_diagnosis} missing Dx
            </Badge>
          )}
        </div>
      </div>

      {/* Sections */}
      <ScrollArea className="flex-1">
        <div className="p-5 max-w-4xl space-y-4">
          {SECTIONS.map(section => {
            const sectionData = data?.sections?.[section.key] || { items: [], count: 0 };
            if (sectionData.count === 0) return null;
            const Icon = section.icon;

            return (
              <div key={section.key} className={`rounded-xl border p-4 ${section.color}`} data-testid={`orders-section-${section.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold">{section.label}</h3>
                    <Badge variant="outline" className="text-[10px]">{sectionData.count}</Badge>
                  </div>
                  <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1"
                    onClick={() => handleBulkFile(section.key)} disabled={bulkLoading}
                    data-testid={`bulk-file-${section.key}`}>
                    {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    File All Ready
                  </Button>
                </div>

                <div className="space-y-1.5">
                  {sectionData.items.map((item, i) => (
                    <OrderItem key={item.id} item={item} onRefresh={fetchData} />
                  ))}
                </div>
              </div>
            );
          })}

          {progress.total === 0 && (
            <div className="text-center py-16">
              <Check className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
              <p className="text-sm font-medium">All clear</p>
              <p className="text-xs text-muted-foreground mt-1">No pending orders or actions</p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Bottom Bar */}
      {progress.total > 0 && (
        <div className="px-5 py-3 border-t bg-card flex items-center justify-between shrink-0" data-testid="orders-bottom-bar">
          <div className="flex items-center gap-2 text-xs">
            {progress.ready_to_sign ? (
              <span className="text-emerald-700 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> All items ready</span>
            ) : (
              <span className="text-amber-700 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {progress.pending} pending</span>
            )}
          </div>
          <Button size="sm" className="h-9 px-6 text-xs gap-1.5"
            disabled={progress.pending > 0 || progress.missing_diagnosis > 0}
            data-testid="orders-sign-all-btn">
            <Check className="w-3.5 h-3.5" /> Sign All
          </Button>
        </div>
      )}
    </div>
  );
}

function OrderItem({ item, onRefresh }) {
  const isComplete = item.status === 'completed';
  const isPendingNurse = item.status === 'pending_nurse';
  const hasLinkage = !!item.linked_diagnosis;

  return (
    <div className={`rounded-md border bg-card p-2.5 flex items-center gap-3 ${isComplete ? 'opacity-50' : ''}`}
      data-testid={`order-item-${item.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium truncate">{item.title}</span>
          <Badge variant="outline" className="text-[8px] shrink-0">{item.item_type?.replace(/_/g, ' ')}</Badge>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
            <User className="w-2.5 h-2.5" /> {item.patient_name}
          </span>
          {hasLinkage ? (
            <span className="flex items-center gap-0.5 text-[9px] text-emerald-600">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {item.linked_diagnosis}
            </span>
          ) : (
            <span className="flex items-center gap-0.5 text-[9px] text-amber-600">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /> No Dx
            </span>
          )}
        </div>
      </div>
      {isComplete ? (
        <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[9px]">Filed</Badge>
      ) : isPendingNurse ? (
        <Badge className="bg-blue-50 text-blue-600 border-blue-200 text-[9px]">With Nurse</Badge>
      ) : (
        <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-600 border-amber-200">Pending</Badge>
      )}
    </div>
  );
}
